/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import get from 'lodash/get';
import React, { useMemo } from 'react';
import { decodeURIFromWidget } from '../../../../utils/decodeCustomCode';
import { AdService } from '../../../components/ad-service';
import { WidgetComponent } from '../../../components/Widget';
import WithArrowConfig from '../../hoc-components/with-arrow-config';
import '../custom-widget-row/widget.m.css';
import { useSelector } from 'react-redux';

// this will be the wrapper inside ahead
const arrowWrapper =
  Component =>
    ({ collection = {}, metadata = {}, index, collectionIndex }) => {
      const config = get(metadata, ['config'], {});
      let {
        adType = '',
        targetingId = '',
        widget = '',
        rowTargetingId = '',
        widgetHeightMobile = '',
        widgetHeightDesktop = ''
      } = config;

      const adComponent = () => {
        return (
          <div className="app-ad app-ad--story-horizontal">
            <AdService
              adtype={adType}
              adTargetingId={targetingId}
              layoutName="PageBuilder"
              slotId={`PB-${get(metadata, ['rowId'], '')}`}
            />
          </div>
        );
      };

      const lightRows = useSelector(state =>
        get(state, ['qt', 'config', 'pagebuilder-config', 'home', 'lightRows'], false)
      );
      const pageType = useSelector(state => get(state, ['qt', 'pageType'], ''));
      const shouldRenderWidgetWrapperBackground = useSelector(state =>
        get(state, ['qt', 'config', 'pagebuilder-config', 'general', 'others', 'enableAdPlaceholderBackground'], false)
      );

      const widgetStyle = {
        '--widget-height-mobile': `${widgetHeightMobile || 50}px`,
        '--widget-height-desktop': `${widgetHeightDesktop || 50}px`
      };
      const widgetId = `widget-slot-${get(metadata, ['rowId'], '')}`;
      // Memoized Widget Component
      const memoizedWidgetComp = () => {
        try {
          if (typeof window !== 'undefined') {
            widget = window.atob(widget);
          } else {
            const bufferedWidget = Buffer.from(widget, 'base64');
            widget = bufferedWidget.toString('utf-8');
          }
        } catch (e) {
          widget = '';
        }
        const newId = `widget-${get(metadata, ['rowId'], '')}`;
        const widgetComp = widgetId => {
          const updatedWidgetCode = decodeURIFromWidget(widget).replace(/\[unique-id]/g, newId);
          return (
            <div className="app-ad app-ad--story-horizontal" styleName={`widget`} style={widgetStyle}>
              <div id={widgetId}>
                <WidgetComponent widgetValue={updatedWidgetCode} />
              </div>
            </div>
          );
        };

        const memoizedWidget = useMemo(() => widgetComp(newId), [newId, shouldRenderWidgetWrapperBackground]);

        return <>{memoizedWidget}</>;
      };

      const widgetWrapper = () => {
        return (
          <div className="app-ad app-ad--story-horizontal" styleName={`widget`} style={widgetStyle}>
            <div id={widgetId}></div>
          </div>
        );
      };

      const shouldRenderWidgetWrapper = lightRows && pageType === 'home-page';
      const updateComp = ({ type }) => {
        switch (type) {
          case 'ad':
            return adComponent;
          case 'widget':
            return shouldRenderWidgetWrapper ? widgetWrapper : memoizedWidgetComp;
          default:
            return null;
        }
      };

      const updateAdAndWidgetComponent = (config = {}) => {
        if (!config.slotConfig) return config;
        const { type } = get(config, ['slotConfig', 0], {});
        config.slotConfig[0].component = updateComp({ type });
        return config;
      };

      return (
        <div id={rowTargetingId || `row-${index}`} style={{ minHeight: '100%', maxWidth: '1172px', margin: 'auto' }}>
          {WithArrowConfig(Component)({ collection, config, index, updateAdAndWidgetComponent, collectionIndex })}
        </div>
      );
    };
export { arrowWrapper };
